// v4 (V4-H2, spec-v4 2.2, 5.1, decision Q2): a channel's settings, at /c/:id/settings (/brand redirects here for the
// main channel; /c/new/settings makes another, up to 10). It grew out of v3's Brand screen: the name, the "Brand"
// section (colours and logo), the narrator and the sign-off stay as they were; the channel adds heading and body
// fonts, an intro and outro clip, and where it publishes (the first of TikTok/Instagram makes new videos vertical).
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { collections, collectionsChanged, writeCurrentChannel, type Clip } from "../collections";
import { ChannelSwitcher, MAX_CHANNELS } from "../components/ChannelSwitcher";
import { Empty, ErrorNote, Icon, Spinner, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { Notice } from "../components/Notice";
import { Shell } from "../components/Shell";
import { director } from "../director";
import { foldSummary } from "../fold";
import { channelSettingsPath, Link, navigate } from "../router";
import { set, toast, useRefresh, useStore } from "../store";
import type { Brand, Channel, PublishPlatform } from "../types";

const DEFAULT_BRAND: Brand = { colors: { bg: "#14161F", fg: "#F5EFE6", accent: "#FFB23E" }, tagline: "", cta: "", ctaUrl: "", logoUrl: null, logoAssetId: null, narrator: null };
const HEX = /^#[0-9a-f]{6}$/i;

/** The renderer's bundled fonts (render/src/font-names.ts, via core CHANNEL_FONT_NAMES). */
export const CHANNEL_FONTS = ["archivo", "inter", "anton", "bebas neue", "dm sans", "poppins", "space grotesk", "playfair display"] as const;
const fontName = (f: string) => f.replace(/\b[a-z]/g, (c) => c.toUpperCase()).replace(/^Dm /, "DM ");
const PLATFORMS: { id: PublishPlatform; name: string; vertical: boolean }[] = [
  { id: "youtube", name: "YouTube", vertical: false },
  { id: "tiktok", name: "TikTok", vertical: true },
  { id: "instagram", name: "Instagram", vertical: true },
  { id: "x", name: "X", vertical: false },
  { id: "linkedin", name: "LinkedIn", vertical: false },
];

export function ChannelScreen({ channelId }: { channelId: string }) {
  const creating = channelId === "new";
  const tick = useRefresh("brand", "me");
  const { data: channels, error, loading, reload } = useLoad(() => api.channels(), [tick]);
  const { data: voices } = useLoad(() => api.library({ kind: "voice_preset" }).catch(() => []), []);
  const { data: logos, setData: setLogos } = useLoad(() => api.library({ kind: "logo" }).catch(() => []), []);
  const { data: clips } = useLoad(() => collections.clips().catch(() => [] as Clip[]), []);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const me = useStore((s) => s.me);
  const ch = creating ? null : (channels?.find((c) => c.id === channelId) ?? null);
  const full = creating && (channels?.length ?? 0) >= MAX_CHANNELS;

  const [name, setName] = useState("");
  const [brand, setBrand] = useState<Brand>(DEFAULT_BRAND);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!channels) return;
    setName(ch?.name ?? "");
    setBrand(ch ? { ...DEFAULT_BRAND, ...ch.brand, colors: { ...DEFAULT_BRAND.colors, ...ch.brand.colors } } : DEFAULT_BRAND);
    setDirty(false);
  }, [channels, ch]);
  useEffect(() => {
    if (ch) writeCurrentChannel(ch.id);
  }, [ch?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (patch: Partial<Brand>) => {
    setBrand((b) => ({ ...b, ...patch, colors: { ...b.colors, ...patch.colors } }));
    setDirty(true);
  };
  const color = (k: keyof Brand["colors"], v: string) => edit({ colors: { ...brand.colors, [k]: v } });
  const logoUrl = brand.logoUrl || logos?.find((l) => l.id === brand.logoAssetId)?.url || null;

  // the logo goes into the library (POST library, base64), and the brand points at it by id
  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast("Use a PNG, JPEG or WebP picture for the logo.", "bad");
    if (file.size > 8 * 1024 * 1024) return toast("That file is over 8 MB. Try a smaller logo.", "bad");
    setUploading(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
        r.onerror = () => reject(new Error("Couldn't read the file."));
        r.readAsDataURL(file);
      });
      const item = await api.saveLibrary({ kind: "logo", name: file.name.replace(/\.[a-z0-9]+$/i, "") || "Logo", mime: file.type, dataBase64, channelId: ch?.id ?? null });
      setLogos([item, ...(logos ?? [])]);
      edit({ logoAssetId: item.id, logoUrl: item.url ?? null });
    } catch (e) {
      toast(`Couldn't upload the logo. ${(e as Error).message}`, "bad");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!name.trim()) {
      toast("Give your channel a name first.", "bad");
      return;
    }
    setSaving(true);
    try {
      const out = wireBrand(brand);
      const next: Channel = ch ? await api.patchChannel(ch.id, { name: name.trim(), brand: out }) : await api.createChannel({ name: name.trim(), brand: out });
      if (me) set({ me: { ...me, channels: ch ? me.channels.map((c) => (c.id === next.id ? next : c)) : [...me.channels, next] } });
      setDirty(false);
      collectionsChanged();
      if (ch) {
        toast("Channel saved. New videos will use it.", "good");
        director.context(`The user saved their channel "${next.name}" by tapping (accent ${next.brand.colors.accent}${next.brand.narrator ? `, narrator ${next.brand.narrator.name}` : ""}).`);
      } else {
        writeCurrentChannel(next.id);
        toast(`${next.name} is ready. Its videos start here.`, "good");
        director.context(`The user made a new channel "${next.name}" (${next.id}) by tapping. It's their current channel now.`);
        navigate(`/c/${encodeURIComponent(next.id)}`, { replace: true });
      }
    } catch (e) {
      toast(`Couldn't save the channel. ${(e as Error).message}`, "bad");
    } finally {
      setSaving(false);
    }
  };

  if (error && !channels)
    return (
      <Shell>
        <div className="page">
          <ErrorNote text={`Couldn't load your channel. ${error}`} retry={reload} />
        </div>
      </Shell>
    );
  if (channels && !creating && !ch)
    return (
      <Shell>
        <div className="page">
          <Empty title="This channel isn't here" action={<Link to="/" className="btn btn-primary">Go home</Link>}>
            It may have been removed, or the link belongs to another account.
          </Empty>
        </div>
      </Shell>
    );

  const intros = (clips ?? []).filter((c) => c.role === "intro");
  const outros = (clips ?? []).filter((c) => c.role === "outro");
  const clipName = (id: string | null | undefined) => (id ? ((clips ?? []).find((c) => c.id === id)?.name ?? "A clip") : null);
  const publish = brand.publishTo ?? [];
  const togglePlatform = (id: PublishPlatform) => edit({ publishTo: publish.some((p) => p.platform === id) ? publish.filter((p) => p.platform !== id) : [...publish, { platform: id }].slice(0, 5) });
  const firstVertical = PLATFORMS.find((p) => p.id === publish[0]?.platform)?.vertical ?? false;

  return (
    <Shell>
      <div className="page brand channel">
        <header className="page-head">
          {ch && <ChannelSwitcher current={ch} to={channelSettingsPath} />}
          <h1 className="page-title">{creating ? "New channel" : "Channel"}</h1>
          <p className="page-lede">
            {creating
              ? "Each channel has its own look, narrator and videos. Switch between them at the top of Home."
              : "Every video on this channel uses its brand, narrator and sign-off. Collections and videos can change any of it."}
          </p>
        </header>
        {full && <Notice tone="warning" text={`You can have up to ${MAX_CHANNELS} channels.`} code="TOO_MANY_CHANNELS" />}
        {loading && !channels ? (
          <Spinner label="Loading" />
        ) : (
          <div className="brand-grid">
            <div className="brand-form">
              <label className="field">
                <span className="field-label">Channel name</span>
                <input value={name} onChange={(e) => (setName(e.target.value), setDirty(true))} placeholder="Your shop, show or brand" maxLength={80} />
              </label>

              <fieldset className="group">
                <legend>Brand</legend>
                <span className="field-label">Colours</span>
                <div className="swatches">
                  {(
                    [
                      ["bg", "Background"],
                      ["fg", "Text"],
                      ["accent", "Accent"],
                    ] as const
                  ).map(([k, label]) => (
                    <label key={k} className="swatch">
                      <input type="color" value={HEX.test(brand.colors[k]) ? brand.colors[k] : "#000000"} onChange={(e) => color(k, e.target.value)} aria-label={`${label} colour`} />
                      <span className="swatch-label">{label}</span>
                      <input className="swatch-hex" value={brand.colors[k]} onChange={(e) => color(k, e.target.value)} aria-label={`${label} hex`} spellCheck={false} maxLength={7} />
                    </label>
                  ))}
                </div>
                <span className="field-label">Logo</span>
                <div className="logo-picks">
                  {logos?.map((l) => (
                    <button type="button" key={l.id} className={`logo-pick${brand.logoAssetId === l.id ? " is-on" : ""}`} onClick={() => edit({ logoAssetId: l.id, logoUrl: l.url ?? null })} aria-label={`Use ${l.name}`} aria-pressed={brand.logoAssetId === l.id}>
                      {l.url ? <img src={l.url} alt="" /> : l.name}
                    </button>
                  ))}
                  <label className={`logo-upload${uploading ? " is-busy" : ""}`}>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void upload(e.target.files?.[0])} disabled={uploading} />
                    <Icon name="plus" size={20} />
                    <span>{uploading ? "Uploading" : "Upload"}</span>
                  </label>
                </div>
                <p className="field-hint">A PNG or WebP with a transparent background looks best on the end card.</p>
              </fieldset>

              <fieldset className="group">
                <legend>Narrator</legend>
                {voices?.length ? (
                  <div className="voices" role="radiogroup" aria-label="Narrator voice">
                    {voices.map((v) => {
                      const voiceId = String(v.meta.voiceId ?? v.id);
                      const on = brand.narrator?.voiceId === voiceId;
                      return (
                        <button type="button" role="radio" aria-checked={on} key={v.id} className={`voice${on ? " is-on" : ""}`} onClick={() => edit({ narrator: { voiceId, name: v.name } })}>
                          <span className="voice-name">{v.name}</span>
                          <span className="voice-desc">{String(v.meta.description ?? "")}</span>
                          {on && <Icon name="check" size={16} />}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="muted">Your Director picks a narrator for each video until you choose one. Ask it to “find a warm, friendly narrator” and it'll appear here.</p>
                )}
              </fieldset>

              <fieldset className="group">
                <legend>Sign-off</legend>
                <label className="field">
                  <span className="field-label">Tagline</span>
                  <input value={brand.tagline ?? ""} onChange={(e) => edit({ tagline: e.target.value })} placeholder="Bread worth getting up for." />
                </label>
                <label className="field">
                  <span className="field-label">Call to action</span>
                  <input value={brand.cta ?? ""} onChange={(e) => edit({ cta: e.target.value })} placeholder="Pre-order by Thursday" />
                </label>
                <label className="field">
                  <span className="field-label">Website or handle</span>
                  <input value={brand.ctaUrl ?? ""} onChange={(e) => edit({ ctaUrl: e.target.value })} placeholder="kettleandcrumb.com" />
                </label>
              </fieldset>

              <div className="channel-more">
                <Fold id="channel.fonts" title="Fonts" summary={foldSummary([brand.fonts?.heading && fontName(brand.fonts.heading), brand.fonts?.body && fontName(brand.fonts.body)]) || "The look's own"}>
                  <div className="channel-fonts">
                    {(["heading", "body"] as const).map((k) => (
                      <label key={k} className="field">
                        <span className="field-label">{k === "heading" ? "Headings and titles" : "Captions and body text"}</span>
                        <span className="select">
                          <select value={brand.fonts?.[k] ?? ""} onChange={(e) => edit({ fonts: { ...brand.fonts, [k]: e.target.value || undefined } })}>
                            <option value="">The look's own</option>
                            {CHANNEL_FONTS.map((f) => (
                              <option key={f} value={f}>
                                {fontName(f)}
                              </option>
                            ))}
                          </select>
                        </span>
                      </label>
                    ))}
                  </div>
                </Fold>

                <Fold id="channel.intro" title="Intro and outro" summary={foldSummary([clipName(brand.introAssetId) && `Intro: ${clipName(brand.introAssetId)}`, clipName(brand.outroAssetId) && `Outro: ${clipName(brand.outroAssetId)}`]) || "None"}>
                  <p className="field-hint">A clip of 10 seconds or less, played before the first scene or after the end card of every new version.</p>
                  {(["intro", "outro"] as const).map((r) => {
                    const pool = r === "intro" ? intros : outros;
                    const key = r === "intro" ? "introAssetId" : "outroAssetId";
                    return (
                      <div key={r} className="channel-clips" role="radiogroup" aria-label={r === "intro" ? "Intro clip" : "Outro clip"}>
                        <span className="field-label">{r === "intro" ? "Intro" : "Outro"}</span>
                        {pool.length ? (
                          <div className="clip-picks">
                            {pool.map((c) => {
                              const on = brand[key] === c.id;
                              return (
                                <button type="button" role="radio" aria-checked={on} key={c.id} className={`clip-pick${on ? " is-on" : ""}`} onClick={() => edit({ [key]: c.id })}>
                                  <span className="clip-pick-art">{c.thumbUrl ? <img src={c.thumbUrl} alt="" /> : <Icon name="film" size={20} />}</span>
                                  <span className="clip-pick-name">{c.name}</span>
                                  {c.durationS ? <span className="clip-pick-len">{Math.round(c.durationS)} s</span> : null}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="muted channel-empty">No {r} clips yet. Add a short clip to your library and mark it as an {r}.</p>
                        )}
                      </div>
                    );
                  })}
                </Fold>

                <Fold id="channel.publish" title="Publish to" summary={publish.map((p) => PLATFORMS.find((x) => x.id === p.platform)?.name ?? p.platform).join(", ") || "Not set"}>
                  <div className="platforms" role="group" aria-label="Where this channel publishes">
                    {PLATFORMS.map((p) => {
                      const on = publish.some((x) => x.platform === p.id);
                      return (
                        <button key={p.id} type="button" className={`chip${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => togglePlatform(p.id)}>
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                  {publish.map((p, i) => (
                    <label key={p.platform} className="field platform-handle">
                      <span className="field-label">{PLATFORMS.find((x) => x.id === p.platform)?.name} handle</span>
                      <input
                        value={p.handle ?? ""}
                        maxLength={80}
                        placeholder="@yourchannel"
                        onChange={(e) => edit({ publishTo: publish.map((x, j) => (j === i ? { platform: x.platform, ...(e.target.value ? { handle: e.target.value } : {}) } : x)) })}
                      />
                    </label>
                  ))}
                  <p className="field-hint">{publish.length ? (firstVertical ? "New videos start vertical (9:16), for the first one on the list." : "New videos keep each type's usual shape.") : "Pick where you post. The first one sets the shape new videos start in."}</p>
                </Fold>
              </div>

              {!creating && (
                <p className="channel-add muted">
                  {(channels?.length ?? 0) >= MAX_CHANNELS ? (
                    `You have ${MAX_CHANNELS} channels, the most there can be.`
                  ) : (
                    <>
                      Making videos for someone else too? <Link to="/c/new/settings">Add a channel</Link>
                    </>
                  )}
                </p>
              )}
            </div>

            <div className="brand-preview-col">
              <p className="panel-label">End card preview</p>
              <EndCard name={name} brand={brand} logoUrl={logoUrl} />
              <div className="save-bar">
                <button data-action="channel.save" type="button" className="btn btn-primary btn-block" onClick={() => void save()} disabled={saving || full || (!dirty && !!ch)}>
                  {saving ? "Saving" : ch ? (dirty ? "Save channel" : "Saved") : "Create channel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

/** What PATCH/POST /channels takes: v4 keys are left out when empty (the api's schema has no null for them). */
export function wireBrand(b: Brand): Partial<Brand> {
  const { introAssetId, outroAssetId, fonts, publishTo, ...rest } = b;
  const f = fonts ? Object.fromEntries(Object.entries(fonts).filter(([, v]) => !!v)) : null;
  return {
    ...rest,
    ...(introAssetId ? { introAssetId } : {}),
    ...(outroAssetId ? { outroAssetId } : {}),
    ...(f && Object.keys(f).length ? { fonts: f } : {}),
    ...(publishTo ? { publishTo: publishTo.map((p) => ({ platform: p.platform, ...(p.handle?.trim() ? { handle: p.handle.trim() } : {}) })) } : {}),
  };
}

function EndCard({ name, brand, logoUrl }: { name: string; brand: Brand; logoUrl: string | null }) {
  const c = brand.colors;
  const heading = brand.fonts?.heading ? `"${fontName(brand.fonts.heading)}", var(--display)` : undefined;
  return (
    <div className="endcard" style={{ background: c.bg, color: c.fg }}>
      <span className="endcard-bar" style={{ background: c.accent }} />
      <div className="endcard-body">
        {logoUrl ? <img className="endcard-logo" src={logoUrl} alt="" /> : <span className="endcard-mono" style={{ borderColor: c.accent, color: c.accent }}>{(name || "B").slice(0, 1).toUpperCase()}</span>}
        <p className="endcard-name" style={heading ? { fontFamily: heading } : undefined}>
          {name || "Your channel"}
        </p>
        {brand.tagline && <p className="endcard-tag">{brand.tagline}</p>}
        <span className="endcard-cta" style={{ background: c.accent, color: c.bg }}>
          {brand.cta || "Your call to action"}
        </span>
        {brand.ctaUrl && <p className="endcard-url">{brand.ctaUrl}</p>}
      </div>
    </div>
  );
}
